import React, { useEffect, useState, useRef } from 'react';
import { api } from '../../api';
import { printTyroCardReceipt, printTyroCertReceipts } from '../../lib/tyroReceiptPrint';

// Full-screen green splash shown immediately after a sale commits.
//
// Kitchen dockets print from the iPad. Cafe stickers are queued in Azure;
// this splash polls job status and shows printed / failed (never blocks
// Next sale). Tyro card receipts print on request — or automatically when
// cert auto-print is on (Admin → Payments → Cert auto-print receipts /
// TYRO_CERT_AUTO_RECEIPTS).
//
// Auto-dismisses after a few seconds so the next customer can be served,
// except on cash sales (change or exact) — staff must tap Next sale.
// Tap-anywhere also dismisses (except on cash sales, or pressing action buttons).
const AUTO_DISMISS_MS = 6000;

function paymentLabel(method, meta) {
  if (method === 'split') {
    const cash = Number(meta?.cash_amount || 0).toFixed(2);
    const card = Number(meta?.card_amount || 0).toFixed(2);
    return `Split • cash $${cash} + card $${card}`;
  }
  if (method === 'cash') {
    if (meta && meta.amount_paid !== undefined) {
      return `Cash • paid $${Number(meta.amount_paid).toFixed(2)}`;
    }
    return 'Cash sale';
  }
  if (method === 'card_paid') return 'EFTPOS';
  return method || 'Sale';
}

export default function PosCompletionSplash({
  lines,
  total,
  paymentMethod,
  counterMeta,
  payRequestId,
  hasReceipts: _hasReceipts,
  certAutoReceipts = false,
  cafeOrderIds = [],
  onDone,
}) {
  const [printing, setPrinting] = useState(false);
  const [printMsg, setPrintMsg] = useState('');
  const autoPrintedRef = useRef(false);
  const [stickerStatus, setStickerStatus] = useState(null);
  const [stickerReprintBusy, setStickerReprintBusy] = useState(false);
  const [stickerReprintNonce, setStickerReprintNonce] = useState(0);
  const cafeIdsKey = (cafeOrderIds || []).join(',');
  // Show print whenever this was a Pay Instore card sale. Receipt text may
  // arrive slightly after approval; the print endpoint fetches from Tyro if needed.
  const showReceiptBtn = Boolean(payRequestId);
  const cashChange = paymentMethod === 'cash' && counterMeta
    ? Number(counterMeta.change_given || 0)
    : null;
  const hasCashChange = cashChange !== null && cashChange > 0.005;
  const isCashSale = paymentMethod === 'cash';
  // Card receipts and every cash sale stay up until staff taps Next sale.
  const holdUntilDismiss = showReceiptBtn || isCashSale;

  useEffect(() => {
    if (holdUntilDismiss) return undefined;
    const t = setTimeout(onDone, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [onDone, holdUntilDismiss]);

  useEffect(() => {
    if (!certAutoReceipts || !payRequestId || autoPrintedRef.current) return undefined;
    autoPrintedRef.current = true;
    let cancelled = false;
    (async () => {
      setPrinting(true);
      setPrintMsg('Printing card receipts…');
      try {
        await printTyroCertReceipts(payRequestId);
        if (!cancelled) setPrintMsg('Card receipts sent to printer.');
      } catch (err) {
        if (!cancelled) setPrintMsg(err.message || 'Auto-print failed — use Print card receipt');
      } finally {
        if (!cancelled) setPrinting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [certAutoReceipts, payRequestId]);

  useEffect(() => {
    if (!cafeIdsKey) return undefined;
    const ids = cafeIdsKey.split(',').map((n) => Number(n)).filter((n) => n > 0);
    if (ids.length === 0) return undefined;

    let cancelled = false;
    let timer = null;
    const started = Date.now();
    const terminal = new Set(['printed', 'failed', 'partial', 'unknown']);

    const run = async () => {
      try {
        const data = await api.getPosCafeStickerStatus(ids, Date.now() - started);
        if (cancelled) return;
        setStickerStatus(data);
        if (!terminal.has(data?.state)) {
          timer = setTimeout(run, 1500);
        }
      } catch {
        if (cancelled) return;
        if (Date.now() - started >= 8000) {
          setStickerStatus({
            state: 'unknown',
            tone: 'unknown',
            message: "Can't confirm stickers — reprint if nothing comes out",
            can_reprint: true,
          });
          return;
        }
        timer = setTimeout(run, 1500);
      }
    };

    setStickerStatus({
      state: 'waiting_enqueue',
      tone: 'pending',
      message: 'Stickers: sending…',
      can_reprint: false,
    });
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [cafeIdsKey, stickerReprintNonce]);

  const uniqueKitchens = [...new Set(lines.map((l) => l.kitchenName))];

  const handleReprintStickers = async (e) => {
    e.stopPropagation();
    const ids = cafeIdsKey.split(',').map((n) => Number(n)).filter((n) => n > 0);
    if (ids.length === 0 || stickerReprintBusy) return;
    setStickerReprintBusy(true);
    try {
      await api.reprintPosCafeStickers(ids);
      setStickerStatus({
        state: 'waiting_enqueue',
        tone: 'pending',
        message: 'Stickers: sending…',
        can_reprint: false,
      });
      setStickerReprintNonce((n) => n + 1);
    } catch (err) {
      setStickerStatus((prev) => ({
        ...(prev || {}),
        message: err.message || 'Reprint failed',
        can_reprint: true,
        tone: 'bad',
      }));
    } finally {
      setStickerReprintBusy(false);
    }
  };

  const handlePrintReceipt = async (e) => {
    e.stopPropagation();
    if (!payRequestId || printing) return;
    setPrinting(true);
    setPrintMsg('');
    try {
      await printTyroCardReceipt(payRequestId, { which: 'customer' });
      setPrintMsg('Card receipt sent to printer.');
    } catch (err) {
      setPrintMsg(err.message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="pos-completion-backdrop" onClick={isCashSale ? undefined : onDone}>
      <div className="pos-completion" onClick={(e) => e.stopPropagation()}>
        <div className="pos-completion-check" aria-hidden>&#10003;</div>
        <h2 className="pos-completion-title">Sale complete</h2>

        <div className="pos-completion-orders">
          {lines.map((l, idx) => (
            <div key={`${l.orderNumber}-${idx}`} className="pos-completion-order-row">
              <span className="pos-completion-order-num">#{l.orderNumber}</span>
              <span>{l.kitchenName}</span>
              <span>${Number(l.total).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="pos-completion-total">Total ${Number(total).toFixed(2)}</div>
        {hasCashChange && (
          <div className="pos-completion-change" role="status" aria-live="polite">
            <div className="pos-completion-change-label">Change</div>
            <div className="pos-completion-change-amount">${cashChange.toFixed(2)}</div>
          </div>
        )}
        {paymentMethod === 'cash' && cashChange !== null && !hasCashChange && (
          <div className="pos-completion-change pos-completion-change-exact" role="status">
            Exact — no change
          </div>
        )}
        <div className="pos-completion-meta">
          {paymentLabel(paymentMethod, counterMeta)}<br />
          Printed for: {uniqueKitchens.join(', ') || '—'}
          {showReceiptBtn ? <><br />Card receipt available on request</> : null}
        </div>

        {stickerStatus?.message && (
          <div
            className={`pos-completion-stickers pos-completion-stickers-${stickerStatus.tone || 'pending'}`}
            role="status"
            aria-live="polite"
          >
            {stickerStatus.message}
          </div>
        )}

        {printMsg && <div className="pos-completion-print-msg">{printMsg}</div>}

        <div className="pos-completion-actions">
          {stickerStatus?.can_reprint && (
            <button
              type="button"
              className="pos-completion-receipt"
              onClick={handleReprintStickers}
              disabled={stickerReprintBusy}
            >
              {stickerReprintBusy ? 'Reprinting…' : 'Reprint stickers'}
            </button>
          )}
          {showReceiptBtn && (
            <button
              type="button"
              className="pos-completion-receipt"
              onClick={handlePrintReceipt}
              disabled={printing}
            >
              {printing ? 'Printing…' : 'Print card receipt'}
            </button>
          )}
          <button className="pos-completion-dismiss" onClick={onDone}>
            Next sale
          </button>
        </div>
      </div>
    </div>
  );
}
